import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — what Finland Oy" },
      { name: "description", content: "The terms that govern your use of the what Finland guide, tickets, and related services." },
      { property: "og:title", content: "Terms of Service — what Finland Oy" },
      { property: "og:description", content: "Terms that govern your use of what Finland services." },
      { property: "og:url", content: "https://whatfinland.com/terms" },
    ],
    links: [{ rel: "canonical", href: "https://whatfinland.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="min-h-screen bg-[#f4f6fb] text-[#0b1230]">
      <section className="relative overflow-hidden bg-[linear-gradient(180deg,#0b1230_0%,#111a44_55%,#2a3470_100%)] text-white">
        <div className="mx-auto max-w-4xl px-6 py-20 md:py-28">
          <p className="font-mono uppercase tracking-[0.28em] text-xs text-white/60 mb-4">Legal</p>
          <h1 className="font-heading text-4xl md:text-6xl leading-tight">Terms of Service</h1>
          <p className="mt-4 text-white/70">Last updated: June 1, 2026</p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-6 py-16 space-y-10 leading-relaxed">
        <Section title="1. Agreement">
          <p>
            These Terms govern your use of the websites, apps, and services provided by what Finland Oy
            (business ID issued in Finland). By using the service you agree to these Terms.
          </p>
        </Section>

        <Section title="2. Eligibility">
          <p>You must be at least 16 years old, or have guardian consent, to use the service.</p>
        </Section>

        <Section title="3. Accounts">
          <ul className="list-disc pl-6 space-y-2">
            <li>You are responsible for activity under your account.</li>
            <li>Keep your credentials confidential and notify us of unauthorized use.</li>
            <li>We may suspend accounts that violate these Terms.</li>
          </ul>
        </Section>

        <Section title="4. Acceptable Use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li>Misuse the service, scrape at scale, or bypass technical limits.</li>
            <li>Upload unlawful, infringing, harmful, or misleading content.</li>
            <li>Impersonate others or misrepresent affiliation.</li>
          </ul>
        </Section>

        <Section title="5. Bookings & Tickets">
          <p>
            Tickets and bookings made through the service are subject to the terms of the relevant venue or
            operator. Refunds, cancellations, and changes follow that operator's policy unless stated otherwise.
          </p>
        </Section>

        <Section title="6. Payments">
          <p>
            Prices include applicable VAT unless stated otherwise. Payments are processed by our payment
            partner. By submitting a payment you authorize the charge.
          </p>
        </Section>

        <Section title="7. Content & License">
          <p>
            You retain ownership of content you submit. You grant what Finland Oy a worldwide, non-exclusive,
            royalty-free license to host, display, and distribute that content as needed to operate the service.
          </p>
        </Section>

        <Section title="8. Intellectual Property">
          <p>
            The service, including text, design, logos, and software, is owned by what Finland Oy or its
            licensors and protected by IP laws.
          </p>
        </Section>

        <Section title="9. Disclaimers">
          <p>
            The service is provided "as is" without warranties of any kind. We do not guarantee accuracy of
            listings, prices, or availability provided by third parties.
          </p>
        </Section>

        <Section title="10. Limitation of Liability">
          <p>
            To the maximum extent permitted by law, what Finland Oy is not liable for indirect, incidental, or
            consequential damages. Our aggregate liability is limited to amounts paid by you to us in the 12
            months preceding the claim.
          </p>
        </Section>

        <Section title="11. Termination">
          <p>
            You may stop using the service at any time. We may suspend or terminate access for violations or
            legal reasons.
          </p>
        </Section>

        <Section title="12. Governing Law">
          <p>
            These Terms are governed by the laws of Finland. Disputes are resolved by the District Court of
            Helsinki, unless mandatory consumer law provides otherwise.
          </p>
        </Section>

        <Section title="13. Changes">
          <p>
            We may update these Terms. Continued use after changes means you accept the updated Terms.
          </p>
        </Section>

        <Section title="14. Contact">
          <p>
            Questions: <a className="underline" href="mailto:info@whatfinland.com">info@whatfinland.com</a>.
          </p>
        </Section>

        <div className="pt-8 border-t border-black/10 flex flex-wrap gap-4 text-sm font-mono uppercase tracking-[0.22em]">
          <Link to="/privacy" className="underline">Privacy Policy</Link>
          <Link to="/contact" className="underline">Contact</Link>
        </div>
      </article>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-heading text-2xl md:text-3xl mb-3">{title}</h2>
      <div className="text-[#0b1230]/85">{children}</div>
    </section>
  );
}
